import React from 'react';
import { Avatar } from 'antd';
import { ProductCategory } from 'api/types/product';
import ProductCartLabel from 'components/ProductCard/ProductCartLabel';
import { isExternalLink, isPreorderProduct, isRequest as isRequestProduct } from 'utils/product';
import { CardHeader, Link, Modal, Typography } from 'ui-kit';
import { getCategoryForProductCard } from 'components/ProductCard/utils';
import placeholder from 'components/Dashboard/Store/Products/images/placeholder.png';
import './LocalProductPreviewModal.scss';
import { formatCurrency } from 'utils';
import { UrlText } from 'components/UrlText';
import { LocalProduct } from 'api/types/localproduct';
import OwnerProfitTooltip from 'ui-kit/OwnerProfitTooltip';

interface Props {
  product: LocalProduct;
  isOpen: boolean;
  onClose: () => void;
}

const BASE_CLASS = 'PreviewProductModal';

const LocalProductPreviewModal: React.FC<Props> = ({ product, isOpen, onClose }) => {
  const {
    name,
    description,
    category,
    subcategory,
    image,
    instructions,
    price,
    ownerLocalProductAmount,
    ownerLocalProductCommission,
    affiliateLink,
    externalLink,
    currency,
  } = product ?? {};

  const isPreorder = isPreorderProduct(product);
  const isRequest = isRequestProduct(product);
  const isLink = isExternalLink(product);
  const getStoreAffiliateLink = (baseUrl: string | undefined) => {
    if (!baseUrl) return '';
    try {
      const url = new URL(baseUrl);
      return url.href;
    } catch (e) {
      if (e instanceof TypeError) {
        // eslint-disable-next-line no-console
        console.error('Invalid URL', baseUrl);
        return '';
      }
      throw e;
    }
  };

  const externalUrl =
    category === ProductCategory.LINK ? externalLink : getStoreAffiliateLink(affiliateLink);
  return (
    <Modal className={BASE_CLASS} visible={isOpen} onCancel={onClose} footer={null}>
      <div className={`${BASE_CLASS}-container`}>
        <div className={`${BASE_CLASS}-container-media`}>
          {isPreorder && !isRequest && <ProductCartLabel label="Pre-order" />}
          {isRequest && <ProductCartLabel label="Request" />}
          <Avatar className={`${BASE_CLASS}-container-media__image`} src={image || placeholder} />
        </div>
        <div className={`${BASE_CLASS}-container-content`}>
          <CardHeader
            title={name}
            subheader={getCategoryForProductCard(category, subcategory, description)}
          />
          <div className={`${BASE_CLASS}-container-content__prices`}>
            {!isLink && (
              <Typography variant="h6" className={`${BASE_CLASS}-container-content__prices-title`}>
                {formatCurrency(price, currency)}
              </Typography>
            )}
            {!isLink && (
              <>
                <Typography variant="caption">
                  YOU MAKE{' '}
                  <Typography
                    variant="body1"
                    color="secondary"
                    className={`${BASE_CLASS}-container-content__prices-title`}
                  >
                    {formatCurrency(ownerLocalProductAmount, currency)}
                  </Typography>
                </Typography>{' '}
                <OwnerProfitTooltip />
              </>
            )}
            {isLink && (
              <>
                <Typography variant="caption">
                  YOU MAKE{' '}
                  <Typography
                    variant="body1"
                    color="primary"
                    className={`${BASE_CLASS}-container-content__prices-title`}
                  >
                    {parseFloat(ownerLocalProductCommission) * 100}% of sales made using your link
                  </Typography>
                </Typography>{' '}
                <OwnerProfitTooltip />
              </>
            )}
          </div>
          <Typography variant="body2" className={`${BASE_CLASS}-container-content__description`}>
            <UrlText text={description || ''} />
          </Typography>
          {isLink && (
            <>
              <div className={`${BASE_CLASS}-container-content__affiliate-info`}>
                <Typography
                  variant="subtitle2"
                  className={`${BASE_CLASS}-container-content__affiliate-subtitle`}
                >
                  Link:
                </Typography>
                <Typography
                  variant="body2"
                  className={`${BASE_CLASS}-container-content__affiliate-link`}
                >
                  <Link target="_blank" href={externalUrl}>
                    {externalUrl}
                  </Link>
                </Typography>
              </div>
              <div className={`${BASE_CLASS}-container-content__affiliate-info`}>
                <Typography
                  variant="subtitle2"
                  className={`${BASE_CLASS}-container-content__affiliate-subtitle`}
                >
                  Disclaimer:
                </Typography>
                <Typography
                  variant="body2"
                  className={`${BASE_CLASS}-container-content__affiliate-info-disclaimer`}
                >
                  Guest must complete purchase using your link in order for you to receive
                  commission. Purchases may take up to 6 weeks to process.
                </Typography>
              </div>
            </>
          )}
          {!isLink && (
            <div className={`${BASE_CLASS}-container-content__instructions`}>
              <Typography
                variant="subtitle2"
                className={`${BASE_CLASS}-container-content__instructions-subtitle`}
              >
                Instructions for guests after purchase:
              </Typography>
              <Typography
                variant="body2"
                className={`${BASE_CLASS}-container-content__instructions-paragraph`}
              >
                {instructions ? <UrlText text={instructions} /> : '-'}
              </Typography>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default LocalProductPreviewModal;
